import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ══════════════════════════════════════════════════════
//  ✅ UPDATED — SHIPPING LOGIC (matches Checkout.js/Cart.js)
//  • Andhra Pradesh & Telangana → base ₹80
//  • All other states           → base ₹100
//  • Blouses: base covers first 5 (was 4), +₹15 for every extra
//  • Sarees:  base covers first 2, +₹15 for every extra (new —
//    rate not specified, using same ₹15/unit pattern as blouses)
//  • Mul Cotton / Kadhi Cotton rules kept as before
//  • Mixed cart: highest category charge wins
//  • ✅ COMBO (sarees / mul cotton, with or without blouses, nothing else):
//      1 saree  → ₹80 AP & Telangana / ₹100 other states, 1 blouse FREE
//      each extra blouse → +₹5      each extra saree → +₹5
//  • ✅ BLOUSES ONLY (pan-India): up to 4 blouses ₹79, each extra +₹5
// ══════════════════════════════════════════════════════
// The logged-in customer's email. Google/OTP login stores it in different places,
// so look in all of them — never fall back to "guest" if the customer is signed in.
const getUserEmail = () => {
  const tryJSON = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null")?.email || ""; } catch { return ""; } };
  return (localStorage.getItem("userEmail") || tryJSON("userData") || tryJSON("user") || tryJSON("currentUser") || "").trim().toLowerCase();
};

const AP_TS_STATES = ["Andhra Pradesh", "Telangana"];
const getStateShippingRate = (stateName) => {
  if (!stateName) return 100;
  const normalized = stateName.trim().toLowerCase();
  const isAPTS = AP_TS_STATES.some(s => s.toLowerCase() === normalized);
  return isAPTS ? 80 : 100;
};
// Blouse styles are now categories of their own ("Round Neck – Front Hook",
// "Full Sleeves – Without Hook", "DTF Print – …") that don't contain the
// word "blouse", so match those names too.
const isBlouse = (item) =>
  /blouse|sleeve|neck|dtf print/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);
const isSaree = (item) =>
  /saree|sari/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);
const isMulCotton = (item) =>
  /mul\s*cotton/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);
const isKadhiCotton = (item) =>
  /kadhi?\s*cotton/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);

const BLOUSE_FREE_QTY   = 4;   // blouses only: ₹79 covers up to 4
const BLOUSE_EXTRA_RATE = 5;   // blouses only: +₹5 for each blouse after that
const BLOUSE_BASE_RATE  = 79;  // ✅ flat pan-India rate for
const COMBO_EXTRA_RATE  = 5;   // ₹5 per extra blouse / extra saree in a combo
                                // blouses only; no longer varies by
                                // state like every other category
const SAREE_FREE_QTY    = 2;  // ✅ new
const SAREE_EXTRA_RATE  = 15; // ⚠️ assumption — not specified, same as blouse rate

const calculateShipping = (items, stateName) => {
  if (!items || items.length === 0) return 0;
  const BASE_RATE = getStateShippingRate(stateName);
  let blouseQty = 0, sareeQty = 0, mulQty = 0, kadhiQty = 0, otherQty = 0;
  items.forEach(item => {
    const qty = item.quantity || 1;
    if      (isBlouse(item))      blouseQty += qty;
    else if (isSaree(item))       sareeQty  += qty;
    else if (isMulCotton(item))   mulQty    += qty;
    else if (isKadhiCotton(item)) kadhiQty  += qty;
    else                          otherQty  += qty;
  });
  // ✅ COMBO SHIPPING — sarees / mul cotton with or without blouses (and
  // nothing else in the cart):
  //   • the first saree costs the state rate (₹80 AP & Telangana, ₹100 others)
  //     and ONE blouse ships free with it
  //   • every extra blouse  → +₹5
  //   • every extra saree / mul cotton → +₹5
  const comboSarees = sareeQty + mulQty;      // "Mul Cotton" is the saree line
  if (comboSarees >= 1 && kadhiQty === 0 && otherQty === 0) {
    const extraSarees  = comboSarees - 1;
    const extraBlouses = Math.max(0, blouseQty - 1);
    return BASE_RATE + (extraSarees + extraBlouses) * COMBO_EXTRA_RATE;
  }

  // ✅ Blouses now use the flat BLOUSE_BASE_RATE (₹80, pan-India)
  // instead of the state-dependent BASE_RATE — saree/mul/kadhi/other
  // below are completely untouched, still state-based as before.
  const blouseCharge = blouseQty > 0 ? BLOUSE_BASE_RATE + Math.max(0, blouseQty - BLOUSE_FREE_QTY) * BLOUSE_EXTRA_RATE : 0;
  const sareeCharge  = sareeQty  > 0 ? BASE_RATE + Math.max(0, sareeQty  - SAREE_FREE_QTY)  * SAREE_EXTRA_RATE  : 0;
  const mulCharge    = mulQty    > 0 ? BASE_RATE + Math.max(0, mulQty    - 2) * 40 : 0;
  const kadhiCharge  = kadhiQty  > 0 ? BASE_RATE + Math.max(0, kadhiQty  - 1) * 40 : 0;
  const otherCharge  = otherQty  > 0 ? BASE_RATE : 0;
  const active = [blouseCharge, sareeCharge, mulCharge, kadhiCharge, otherCharge].filter(c => c > 0);
  return active.length > 0 ? Math.max(...active) : 0;
};

export default function Payment({ cartItems, setCartItems }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    finalTotal:   passedFinalTotal,
    address:      checkoutAddress,
    shipping:     passedShipping,
    shippingState,
  } = location.state || {};

  const [selectedMethod,  setSelectedMethod]  = useState("");
  const [selectedOnline,  setSelectedOnline]  = useState("");
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(""); // starts empty
  const [scriptLoaded,    setScriptLoaded]    = useState(false);
  const [scriptError,     setScriptError]     = useState(false);

  // ══════════════════════════════════════════════════════
  //  REAL-TIME STOCK — same pattern as Checkout.jsx.
  //  Payment is the last chance to catch "someone else just
  //  bought this" before money actually moves, so we check
  //  once on load AND once more right at the moment of pay.
  // ══════════════════════════════════════════════════════
  const [stockChecks,   setStockChecks]   = useState({}); // id -> { canAdd, available }
  const [checkingStock, setCheckingStock] = useState(true);

  const getStock = (item) => typeof item.stock === "number" ? item.stock : Infinity;

  const verifyStock = async (items) => {
    const results = {};
    await Promise.all(items.map(async (item) => {
      const id = item._id || item.id;
      try {
        const res = await axios.post(`${BASE}/api/products/check-stock`, {
          productId: id,
          color:     item.color || "",
          size:      item.selectedSize || "",
          quantity:  item.quantity || 1,
        });
        results[id] = { canAdd: !!res.data.canAdd, available: res.data.available };
      } catch {
        const known = getStock(item);
        results[id] = {
          canAdd:    !item.soldOut && known >= (item.quantity || 1),
          available: known,
        };
      }
    }));
    return results;
  };

  useEffect(() => {
    let cancelled = false;
    if (!cartItems || cartItems.length === 0) { setCheckingStock(false); return; }
    setCheckingStock(true);
    verifyStock(cartItems).then(results => {
      if (!cancelled) { setStockChecks(results); setCheckingStock(false); }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isItemAvailable = (item) => {
    const id = item._id || item.id;
    const check = stockChecks[id];
    if (check) return check.canAdd;
    return !item.soldOut && getStock(item) > 0;
  };

  const availableItems   = (cartItems || []).filter(isItemAvailable);
  const unavailableItems = (cartItems || []).filter(item => !isItemAvailable(item));

  // Live totals — recalculated from whatever is actually still
  // purchasable, not the (possibly now-stale) value Checkout passed in.
  const liveProductTotal = availableItems.reduce(
    (sum, item) => sum + Number(item.price) * (item.quantity || 1), 0
  );
  const liveShipping   = shippingState !== undefined
    ? calculateShipping(availableItems, shippingState)
    : (passedShipping ?? 0);
  const liveFinalTotal = unavailableItems.length > 0
    ? liveProductTotal + liveShipping
    : (passedFinalTotal ?? (liveProductTotal + liveShipping));

  // ── Load Razorpay SDK ──────────────────────────────
  useEffect(() => {
    if (window.Razorpay) { setScriptLoaded(true); return; }
    const script    = document.createElement("script");
    script.src      = "https://checkout.razorpay.com/v1/checkout.js";
    script.async    = true;
    script.onload   = () => setScriptLoaded(true);
    script.onerror  = () => {
      setScriptError(true);
      setError("Payment SDK failed to load. Please refresh the page.");
    };
    document.body.appendChild(script);
    return () => { /* don't remove — causes issues on remount */ };
  }, []);

  // ── Razorpay flow ──────────────────────────────────
  const handleRazorpay = (amount, draft) =>
    new Promise(async (resolve, reject) => {
      if (!scriptLoaded) return reject(new Error("Payment SDK not loaded. Please refresh."));

      let orderData;
      try {
        const { data } = await axios.post(`${BASE}/api/payment/create-order`, {
          amount,
          draft,          // cart + address saved on the server BEFORE paying
        }, { timeout: 60000 });
        if (!data.orderId) throw new Error(data.message || "Order creation failed");
        orderData = data;
      } catch (err) {
        return reject(new Error(err.response?.data?.message || err.message || "Could not create payment order."));
      }

      const options = {
        key:         orderData.key,
        amount:      orderData.amount,
        currency:    orderData.currency || "INR",
        name:        "Mana Vastralu",
        description: "Saree Purchase",
        order_id:    orderData.orderId,
        prefill: {
          name:    localStorage.getItem("userName")  || "",
          email:   getUserEmail(),
          contact: checkoutAddress?.phone || "",
        },
        theme: { color: "#5c2e1f" },
        // handler only runs after the money has been taken, so resolve straight away.
        // The server checks the signature itself when it saves the order.
        handler: (response) => resolve({
          paymentId: response.razorpay_payment_id,
          orderId:   response.razorpay_order_id,
          signature: response.razorpay_signature,
        }),
        modal: { ondismiss: () => reject(new Error("cancelled")) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) =>
        reject(new Error(resp.error?.description || "Payment failed"))
      );
      rzp.open();
    });

  // ── Pay button handler ─────────────────────────────
  const handlePay = async () => {
    if (!selectedMethod) { setError("Please select a payment method."); return; }
    if (selectedMethod === "online" && !selectedOnline) {
      setError("Please choose an online payment option."); return;
    }
    if (!cartItems || cartItems.length === 0) { setError("Your cart is empty."); return; }

    setError("");
    setLoading(true);

    // ── FINAL live stock check, right before money moves ──
    const freshResults = await verifyStock(availableItems.length ? availableItems : cartItems);
    setStockChecks(prev => ({ ...prev, ...freshResults }));

    const payableItems = (availableItems.length ? availableItems : cartItems)
      .filter(item => freshResults[(item._id || item.id)]?.canAdd);
    const justSoldOut  = (availableItems.length ? availableItems : cartItems)
      .filter(item => !freshResults[(item._id || item.id)]?.canAdd);

    if (justSoldOut.length > 0) {
      setLoading(false);
      setError(
        justSoldOut.length === 1
          ? `"${justSoldOut[0].name}" just went out of stock and can't be purchased. It has been marked unavailable below — please review your order and try again.`
          : `${justSoldOut.length} items just went out of stock and can't be purchased. They've been marked unavailable below — please review your order and try again.`
      );
      return;
    }

    if (payableItems.length === 0) {
      setLoading(false);
      setError("All items in your order are currently out of stock.");
      return;
    }

    const chargeAmount = payableItems.reduce(
      (sum, item) => sum + Number(item.price) * (item.quantity || 1), 0
    ) + (shippingState !== undefined ? calculateShipping(payableItems, shippingState) : liveShipping);

    try {
      const userEmail  = getUserEmail() || "guest@gmail.com";
      const userName   = localStorage.getItem("userName")  || "Guest";
      const payType    = selectedMethod === "cod" ? "Cash on Delivery" : selectedOnline;

      const products = payableItems.map(item => {
        const rawImg   = item.images?.[0] || item.image || "";
        const imageUrl = rawImg.startsWith("http") ? rawImg
          : rawImg.startsWith("uploads/") ? `${BASE}/${rawImg}`
          : rawImg ? `${BASE}/uploads/${rawImg}` : "";
        return {
          productId:    item._id || item.id,
          name:         item.name,
          price:        Number(item.price),
          quantity:     item.quantity || 1,
          selectedSize: item.selectedSize || "",
          color:        item.color || "",
          image:        imageUrl,
          images:       item.images || [],
        };
      });

      const orderPayload = {
        user_id:        userEmail,
        email:          userEmail,
        userName,
        phone:          checkoutAddress?.phone || "",
        address:        checkoutAddress || {},
        payment_method: payType,
        paymentMethod:  payType,
        total_amount:   Number(chargeAmount),
        totalAmount:    Number(chargeAmount),
        products,
        paymentId:      "",
        paymentStatus:  selectedMethod === "online" ? "Paid" : "Pending",
      };

      let paid = null;
      if (selectedMethod === "online") {
        paid = await handleRazorpay(chargeAmount, orderPayload);
        orderPayload.paymentId          = paid.paymentId;
        orderPayload.razorpay_order_id  = paid.orderId;
        orderPayload.razorpay_signature = paid.signature;
        try { localStorage.setItem("mv_unsaved_payment", JSON.stringify({ paymentId: paid.paymentId, at: Date.now() })); } catch {}
        setError(`Payment received ✔ (ID ${paid.paymentId}). Saving your order — please DON'T close this page or pay again…`);
      }

      // After a payment, retry saving several times (the server may be waking up).
      // Even if every try fails, the server's Razorpay webhook still creates the order.
      let res = null, lastErr = null;
      const tries = paid ? 5 : 1;
      for (let i = 0; i < tries; i++) {
        try {
          res = await axios.post(`${BASE}/add-order`, orderPayload, { timeout: 60000 });
          break;
        } catch (e) {
          lastErr = e;
          if (e.response && e.response.status < 500) break;
          await new Promise(r => setTimeout(r, 2000 * (i + 1)));
        }
      }
      if (!res && paid) {
        setError(`Your payment of ₹${chargeAmount} was received (Payment ID: ${paid.paymentId}). We are finalising your order — it will appear in My Orders shortly. Please do NOT pay again. If it doesn't appear in 10 minutes, WhatsApp us this Payment ID.`);
        return;
      }
      if (!res) throw lastErr;
      try { localStorage.removeItem("mv_unsaved_payment"); } catch {}

      if (res.data.success) {
        // remember this order on the device so My Orders can always find it
        try {
          const mine = JSON.parse(localStorage.getItem("myOrderIds") || "[]");
          localStorage.setItem("myOrderIds", JSON.stringify([...mine, res.data.orderId].slice(-100)));
        } catch {}
        const payableIds = new Set(payableItems.map(i => i._id || i.id));
        const remaining  = (cartItems || []).filter(i => !payableIds.has(i._id || i.id));
        setCartItems(remaining);
        localStorage.setItem("cartItems", JSON.stringify(remaining));
        navigate("/order-success", {
          state: {
            orderId:     res.data.orderId,
            totalAmount: chargeAmount,
            paymentType: payType,
            items:       payableItems,
          },
        });
      } else {
        setError(res.data.error || "Order could not be placed. Please try again.");
      }
    } catch (err) {
      if (err.message === "cancelled") {
        setError("Payment was cancelled. Please try again.");
      } else {
        setError(err.response?.data?.error || err.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getItemImg = (item) => {
    const img = item.images?.[0] || "";
    if (!img) return "/images/s1.png";
    if (img.startsWith("http")) return img;
    if (img.startsWith("uploads/")) return `${BASE}/${img}`;
    return `${BASE}/uploads/${img}`;
  };

  const canPay = scriptLoaded && !scriptError && !loading && !checkingStock && availableItems.length > 0;

  return (
    <>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={s.page}>
        <div style={s.card}>

          {/* Header */}
          <div style={s.header}>
            <div style={{ fontSize:32,marginBottom:6 }}>🔒</div>
            <h2 style={s.title}>Secure Checkout</h2>
            <div style={s.amountBadge}>₹{Number(liveFinalTotal||0).toLocaleString("en-IN")}</div>
            <p style={s.brand}>Mana Vastralu</p>
          </div>

          {/* Order summary */}
          {cartItems?.length > 0 && (
            <div style={{ marginBottom:20,background:"#fdf6ec",border:"1px solid #ecd9bd",borderRadius:12,padding:"12px 16px" }}>
              <div style={{ fontSize:11,fontWeight:700,color:"#8a4a30",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                <span>Order Summary ({availableItems.length} item{availableItems.length!==1?"s":""})</span>
                {checkingStock && <span style={{ fontWeight:500,textTransform:"none",fontSize:11 }}>Checking stock…</span>}
              </div>

              {unavailableItems.length > 0 && (
                <div style={{
                  marginBottom:10, padding:"9px 12px", background:"#fff5f5",
                  border:"1px solid #fecaca", borderRadius:8, fontSize:12,
                  color:"#c62828", fontWeight:600,
                }}>
                  ⚠️ {unavailableItems.length === 1
                    ? "1 item below is out of stock and will not be charged."
                    : `${unavailableItems.length} items below are out of stock and will not be charged.`}
                </div>
              )}

              {cartItems.map((item,i)=>{
                const outOfStock = !isItemAvailable(item);
                return (
                <div key={i} style={{ display:"flex",gap:10,alignItems:"center",marginBottom:8,paddingBottom:8,borderBottom:i<cartItems.length-1?"1px solid #f0e4d6":"none", opacity: outOfStock ? 0.55 : 1 }}>
                  <div style={{ position:"relative", flexShrink:0 }}>
                    <img src={getItemImg(item)} alt={item.name} onError={e=>{e.target.src="/images/s1.png";}}
                      style={{ width:44,height:44,objectFit:"cover",borderRadius:6,border:"1px solid #ecd9bd",filter: outOfStock ? "grayscale(70%)" : "none" }}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:600,color:"#5c2e1f" }}>{item.name}</div>
                    {outOfStock ? (
                      <div style={{ fontSize:11, color:"#c62828", fontWeight:700 }}>Out of stock — unavailable</div>
                    ) : (
                      <div style={{ fontSize:11,color:"#8a4a30" }}>{item.selectedSize&&`Size: ${item.selectedSize} · `}Qty: {item.quantity||1}</div>
                    )}
                  </div>
                  <div style={{ fontSize:13,fontWeight:700,color: outOfStock ? "#c62828" : "#5c2e1f", textDecoration: outOfStock ? "line-through" : "none" }}>
                    ₹{(Number(item.price)*(item.quantity||1)).toLocaleString()}
                  </div>
                </div>
              );})}

              <div style={{ marginTop:8, paddingTop:8, borderTop:"1px dashed #ecd9bd", display:"flex", justifyContent:"space-between", fontSize:12, color:"#5c2e1f" }}>
                <span>Delivery Charges</span>
                <span style={{ fontWeight:700 }}>₹{liveShipping}</span>
              </div>

              {checkoutAddress?.fullName && (
                <div style={{ marginTop:8,paddingTop:8,borderTop:"1px dashed #ecd9bd",fontSize:11,color:"#8a4a30" }}>
                  <strong style={{ color:"#5c2e1f" }}>{checkoutAddress.fullName}</strong>
                  {checkoutAddress.phone&&` · ${checkoutAddress.phone}`}<br/>
                  {[checkoutAddress.houseNo,checkoutAddress.landmark,checkoutAddress.village,checkoutAddress.district,checkoutAddress.state,checkoutAddress.pincode].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          )}

          {/* Error — only shows after user action */}
          {error && (
            <div style={s.errorBox}>
              <span>⚠️</span><span>{error}</span>
            </div>
          )}

          {/* All items unavailable */}
          {!checkingStock && availableItems.length === 0 && (
            <div style={s.errorBox}>
              <span>⚠️</span><span>Everything in this order is currently out of stock. Please go back to your cart.</span>
            </div>
          )}

          {/* SDK loading indicator — not an error */}
          {!scriptLoaded && !scriptError && (
            <div style={{ textAlign:"center",padding:"10px",fontSize:12,color:"#8a4a30",marginBottom:12 }}>
              Loading payment SDK…
            </div>
          )}

          <p style={s.sectionLabel}>Choose Payment Method</p>

          {/* COD — disabled */}
          <div style={{ ...s.option,opacity:0.45,cursor:"not-allowed",background:"#f9f9f9",border:"1.5px solid #e5e7eb" }}>
            <span style={s.optIcon}>💵</span>
            <div style={{ flex:1 }}>
              <div style={{ ...s.optTitle,color:"#999" }}>Cash on Delivery</div>
              <div style={{ ...s.optSub,color:"#bbb" }}>Currently unavailable</div>
            </div>
            <span style={{ fontSize:9,fontWeight:700,color:"#fff",background:"#9ca3af",padding:"3px 8px",borderRadius:20,letterSpacing:"0.5px",textTransform:"uppercase",flexShrink:0 }}>
              Unavailable
            </span>
          </div>

          {/* Online — active */}
          <div style={{ ...s.option,...(selectedMethod==="online"?s.optionActive:{}) }}
            onClick={()=>{ setSelectedMethod("online"); setError(""); }}>
            <span style={s.optIcon}></span>
            <div style={{ flex:1 }}>
              <div style={s.optTitle}>Online Payment</div>
              <div style={s.optSub}>UPI · Cards · Net Banking </div>
            </div>
            <Radio on={selectedMethod==="online"}/>
          </div>

          {selectedMethod==="online" && (
            <div style={s.subWrap}>
              {[
                { value:"UPI / PhonePe / GPay", label:"UPI / PhonePe / GPay", icon:"" },
                { value:"Debit/Credit Card",    label:"Debit / Credit Card",   icon:"" },
                { value:"Net Banking",          label:"Net Banking",           icon:"" },
              ].map(opt=>(
                <div key={opt.value}
                  style={{ ...s.subRow,...(selectedOnline===opt.value?s.subRowActive:{}) }}
                  onClick={()=>{ setSelectedOnline(opt.value); setError(""); }}>
                  <span style={s.subIcon}>{opt.icon}</span>
                  <span style={s.subLabel}>{opt.label}</span>
                  <Radio on={selectedOnline===opt.value}/>
                </div>
              ))}
            </div>
          )}

          <button onClick={handlePay} disabled={!canPay}
            style={{ ...s.payBtn,opacity:!canPay?0.65:1,cursor:!canPay?"not-allowed":"pointer" }}>
            {loading
              ? <><Spinner/>Placing Order…</>
              : checkingStock
                ? "Checking stock…"
                : `Pay ₹${Number(liveFinalTotal||0).toLocaleString("en-IN")} →`
            }
          </button>

          {scriptError && (
            <p style={{ textAlign:"center",fontSize:12,color:"#c62828",marginTop:8 }}>
              Payment SDK failed to load.{" "}
              <span style={{ textDecoration:"underline",cursor:"pointer" }} onClick={()=>window.location.reload()}>
                Click here to refresh
              </span>
            </p>
          )}

          <p style={s.trust}>🔒 256-bit SSL · Powered by Razorpay</p>
        </div>
      </div>
    </>
  );
}

function Radio({ on }) {
  return (
    <div style={{ width:20,height:20,borderRadius:"50%",border:on?"6px solid #5c2e1f":"2px solid #ccc",background:"#fff",flexShrink:0,transition:"all .2s" }}/>
  );
}

function Spinner() {
  return (
    <span style={{ display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,.4)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin .7s linear infinite",marginRight:8,verticalAlign:"middle" }}/>
  );
}

const s = {
  // ✅ CHANGED — was a near-black gradient (#1a0a00 → #3d1a00 → #1a0a00),
  // which made the whole payment page feel like a dark checkout modal.
  // Now a soft warm cream-to-blush gradient using the logo palette, so
  // the page reads as light/premium instead of dark/heavy.
  page:        { minHeight:"100vh",background:"linear-gradient(160deg,#fdf6ec 0%,#f6ddca 50%,#fdf6ec 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"Georgia,serif" },
  card:        { background:"#ffffff",borderRadius:24,padding:"36px 32px",width:"100%",maxWidth:500,boxShadow:"0 24px 60px rgba(92,46,31,.18)" },
  header:      { textAlign:"center",marginBottom:20 },
  title:       { margin:"0 0 10px",fontSize:24,fontWeight:700,color:"#4a2419",letterSpacing:"-0.5px" },
  amountBadge: { display:"inline-block",background:"#5c2e1f",color:"#fff",fontSize:20,fontWeight:700,padding:"4px 20px",borderRadius:100,marginBottom:6 },
  brand:       { margin:0,fontSize:13,color:"#a0522d",letterSpacing:1,textTransform:"uppercase" },
  sectionLabel:{ fontSize:11,fontWeight:700,color:"#999",textTransform:"uppercase",letterSpacing:1.5,marginBottom:10 },
  errorBox:    { display:"flex",alignItems:"center",gap:8,background:"#fff5f5",border:"1px solid #fca5a5",color:"#b91c1c",padding:"12px 16px",borderRadius:12,fontSize:13,marginBottom:18 },
  option:      { display:"flex",alignItems:"center",gap:14,border:"1.5px solid #e5e7eb",borderRadius:14,padding:"16px 18px",marginBottom:12,cursor:"pointer",transition:"all .2s",background:"#fff" },
  optionActive:{ border:"1.5px solid #5c2e1f",background:"#fdf6ec",boxShadow:"0 4px 16px rgba(92,46,31,.1)" },
  optIcon:     { fontSize:24 },
  optTitle:    { fontWeight:600,fontSize:15,color:"#4a2419" },
  optSub:      { fontSize:12,color:"#9ca3af",marginTop:2 },
  subWrap:     { background:"#faf5ef",border:"1.5px solid #e5c9a8",borderRadius:14,padding:"8px 12px",marginBottom:16 },
  subRow:      { display:"flex",alignItems:"center",gap:12,padding:"11px 10px",borderRadius:10,cursor:"pointer",marginBottom:4,transition:"background .15s" },
  subRowActive:{ background:"#f5e6d3" },
  subIcon:     { fontSize:18 },
  subLabel:    { flex:1,fontSize:14,color:"#374151",fontFamily:"sans-serif" },
  payBtn:      { width:"100%",padding:"17px",background:"linear-gradient(90deg,#5c2e1f,#c0622a)",color:"#fff",border:"none",borderRadius:14,fontSize:17,fontWeight:700,letterSpacing:0.4,display:"flex",alignItems:"center",justifyContent:"center",gap:6,boxShadow:"0 8px 24px rgba(92,46,31,.35)",transition:"opacity .2s" },
  trust:       { textAlign:"center",fontSize:12,color:"#aaa",marginTop:14,fontFamily:"sans-serif" },
};